/**
 * Plain-text diagnostic report, designed to be PASTED as-is.
 *
 * Originates from a specific case: the app stayed "syncing" forever and the
 * reason was that the PowerSync service was not even running. From the
 * screen there was no way to distinguish "not connecting" from "connecting and slow",
 * and describing it by hand loses half the data.
 *
 * SINGLE SOURCE OF TRUTH. The first version received its own object while the screen
 * rendered its rows independently: two sources for the same thing, so adding a
 * row to the UI left the report incomplete without anyone noticing. Now
 * both consume the SAME array of sections: the screen renders it, this
 * function serializes it, and the hint is computed by reading it.
 *
 * Each item has three fields for distinct reasons:
 *   · `label`/`value` — already-translated text: what is displayed and what is copied.
 *   · `status`        — only used by the UI for coloring; the report ignores it.
 *   · `raw`           — UNTRANSLATED value, so that logic does not depend on
 *                       the locale. Without it, the hint would fail to trigger as soon
 *                       as someone changed a translation.
 *
 * Does NOT include personal data: no email, no name, no token. The caller constructing
 * the sections is responsible for redacting identifiers.
 */

export type DiagnosticStatus = "success" | "warning" | "danger" | "neutral";

export interface DiagnosticItem {
  /** Stable identifier, independent of locale. Used by logic.*/
  key: string;
  /** Already-translated label. */
  label: string;
  /** Already-translated / formatted value, exactly as displayed on screen.*/
  value: string;
  /** UI only.*/
  status?: DiagnosticStatus;
  /** Raw value for logic that must not depend on locale.*/
  raw?: string | number | boolean | null;
}

export interface DiagnosticSection {
  title: string;
  items: DiagnosticItem[];
}

export interface DiagnosticsReportInput {
  appName: string;
  /** ISO timestamp. Passed in; does not read the clock here (pure function).*/
  timestamp: string;
  sections: DiagnosticSection[];
}

/** Searches by `key`, never by label: labels are translated.*/
function findItem(sections: DiagnosticSection[], key: string): DiagnosticItem | undefined {
  for (const s of sections) {
    const found = s.items.find((i) => i.key === key);
    if (found) {
      return found;
    }
  }
  return undefined;
}

/**
 * Actionable hint for the pattern that originated all of this: session is present
 * and sync does not start. Almost always a downed service, not the app, and
 * stating it here avoids searching in the wrong place.
 */
function hint(sections: DiagnosticSection[]): string[] {
  const sessionItem = findItem(sections, "session");
  const sync = findItem(sections, "sync");
  if (
    sessionItem?.raw !== true ||
    !sync ||
    sync.raw === "connected" ||
    sync.raw === undefined ||
    sync.raw === null
  ) {
    return [];
  }
  return [
    "",
    "## Pista",
    `La sesión está activa pero PowerSync no llega a "connected" (${String(sync.raw)}).`,
    "Comprobar que el servicio de PowerSync responde en la URL de arriba: si da",
    "502 o no responde, el problema es la pila, no la app.",
  ];
}

export function buildDiagnosticsReport(input: DiagnosticsReportInput): string {
  const parts: string[] = [`# Diagnóstico — ${input.appName}`, input.timestamp];

  for (const section of input.sections) {
    partsPush(parts, section);
  }

  parts.push(...hint(input.sections));
  return parts.join("\n");
}

function partsPush(parts: string[], section: DiagnosticSection) {
  parts.push("", `## ${section.title}`);
  for (const item of section.items) {
    // An empty field is MARKED instead of omitted: in a pasted report, a
    // missing datum and an uncollected datum read the same if not specified.
    parts.push(`- ${item.label}: ${item.value === "" ? "(sin dato)" : item.value}`);
  }
}
